import Vue from 'vue'
import api from '@/apis/siteDeviceEventRecord'
import commonApi from '@/apis/common'
import parser from '@/models/siteEventRecord'
import {
  RESET_STATE,
  PROCESS_API_SUCCESS,
  PROCESS_API_FAILURE,
  GET_EVENT_RECORDS_REQUEST,
  GET_EVENT_RECORDS_SUCCESS,
  GET_EVENT_RECORDS_FAILURE,
  GET_RECORD_MEDIA_REQUEST,
  GET_RECORD_MEDIA_SUCCESS,
  GET_RECORD_MEDIA_FAILURE,
} from '@/store/mutation-types'

let initialState = {
  eventsRecords: {},
  recordsMedia: {},
  // eventId → siteId. getRecordMedia가 재생 시점에 view URL을 발급할 때 siteId가 필요한데,
  // 호출부(11곳)가 siteId를 넘기지 않으므로 getEventRecords 시점에 기억해 둔다.
  eventsSiteIds: {},
  status: {
    getEventRecords: null,
    getRecordMedia: {},
  },
}

// initial state
const state = Vue.util.extend({}, initialState)

// getters
const getters = {
  eventRecord: function (state) {
    return function (eventId, recordId) {
      if (state.eventsRecords[eventId] === undefined) return undefined
      return state.eventsRecords[eventId][recordId]
    }
  },
  eventRecords: function (state) {
    return function (eventId) {
      return state.eventsRecords[eventId]
    }
  },
  eventsRecords: function (state) {
    return state.eventsRecords
  },
  statusGetEventRecords: function (state) {
    return state.status.getEventRecords
  },
  recordMedia: function (state) {
    return function (recordId) {
      return state.recordsMedia[recordId]
    }
  },
  recordsMedia: function (state) {
    return state.recordsMedia
  },
  statusGetRecordMedia: function (state) {
    return function (recordId) {
      return state.status.getRecordMedia[recordId]
    }
  },
}

// actions
const actions = {
  getEventRecords: function ({commit}, {siteId, deviceId, eventId}) {
    commit(GET_EVENT_RECORDS_REQUEST)
    return new Promise((resolve, reject) => {
      api.getSiteDeviceEventRecords({siteId, deviceId, eventId}).then(
        async res => {
          let data = res.body
          // CHEKT-1406: 목록에서 url이 제거되면 스냅샷(jpg) URL을 무로그 snapshots 엔드포인트에서
          // 일괄 발급받아 각 jpg record에 주입한다 → 스냅샷 소비처(여러 컴포넌트)가 record.mediaUrl을
          // 그대로 쓸 수 있다. (mp4 재생 URL은 재생 시점에 getRecordMedia가 view로 lazy 발급하여
          // 접근 로그를 최소화한다.) dev 등 아직 url을 내려주는 백엔드는 이미 url이 있으므로 생략(하위호환).
          const needsSnapshotUrls = Array.isArray(data) &&
            data.some((r) => String(r.format).toLowerCase() === 'jpg' && !r.url)
          if (needsSnapshotUrls) {
            try {
              const snapRes = await api.getEventSnapshots({siteId, eventId})
              const snapshotUrls = (snapRes.body && snapRes.body.snapshot_urls) || {}
              data.forEach((r) => {
                if (String(r.format).toLowerCase() === 'jpg' && snapshotUrls[r.record_id]) {
                  r.url = snapshotUrls[r.record_id]
                }
              })
            } catch (e) {
              // best-effort: 스냅샷 발급 실패는 스냅샷만 누락시키고 목록/재생 흐름은 계속 진행한다.
            }
          }
          commit(GET_EVENT_RECORDS_SUCCESS, {data, eventId, siteId})
          resolve(data)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(GET_EVENT_RECORDS_FAILURE)
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
  /**
   * 이벤트 레코드(mp4) 다운로드용 서명 URL을 발급받는다 (records/:recordId/download).
   *
   * 목록에서 url이 제거되어(CHEKT-1406) 다운로드 시점에 발급한다. 재생(view)과 권한/로그가 다르다
   * (SITE_EVENT_RECORD_DOWNLOAD_ACTION, action=downloaded). Promise를 url 문자열로 resolve한다.
   *
   * @param {Object} ctx - Vuex 컨텍스트 (commit 사용)
   * @param {Object} payload
   * @param {number|string} payload.siteId   - 사이트 아이디
   * @param {number|string} payload.eventId  - 이벤트 아이디
   * @param {number|string} payload.recordId - 레코드 record_id
   * @returns {Promise<string>} 서명된 다운로드 URL
   */
  getEventRecordDownloadUrl: function ({commit}, {siteId, eventId, recordId}) {
    return new Promise((resolve, reject) => {
      api.getEventRecordDownloadUrl({siteId, eventId, recordId}).then(
        res => {
          resolve(res.body?.url)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
  getRecordMedia: async function ({commit, getters, state}, {eventId, recordIndex, recordId, url}) {
    commit(GET_RECORD_MEDIA_REQUEST, {recordId})
    try {
      // CHEKT-1406: mp4(영상 재생)는 목록의 url(하위호환용, 구 앱 전용)을 쓰지 않고 항상 view 엔드포인트로
      // 재생 URL을 발급받는다 → 실제 재생당 접근로그(action=viewed)를 남긴다. 재생 시점에만 호출.
      // jpg 등 스냅샷은 무로그 엔드포인트 설계이므로 목록 url(있으면)을 그대로 쓴다(불필요한 로그 방지).
      // siteId는 getEventRecords가 기억해 둔 값.
      const isMp4 = ('' + getters.eventsRecords[eventId][recordIndex].format).toLowerCase() === 'mp4'
      let mediaSourceUrl = url
      if (isMp4 || !mediaSourceUrl) {
        const siteId = state.eventsSiteIds[eventId]
        const viewRes = await api.getEventRecordViewUrl({siteId, eventId, recordId})
        mediaSourceUrl = viewRes.body && viewRes.body.url
      }
      let res = await commonApi.downloadImage(mediaSourceUrl)
      const blob = res.body
      let codec = ''
      if (('' + getters.eventsRecords[eventId][recordIndex].format).toLowerCase() === 'mp4') {
        codec = await Vue.tool.getCodecFromMP4(blob)
      }
      commit(GET_RECORD_MEDIA_SUCCESS, {eventId, recordIndex, recordId, blob, codec})
    } catch (err) {
      commit(GET_RECORD_MEDIA_FAILURE, {recordId})
    }
  },
}

// mutations
const mutations = {
  [RESET_STATE]: function (state) {
    for (let f in state) {
      Vue.set(state, f, initialState[f])
    }
  },
  [GET_EVENT_RECORDS_REQUEST]: function (state) {
    state.status.getEventRecords = "requested"
  },
  [GET_EVENT_RECORDS_SUCCESS]: function (state, {data, eventId, siteId}) {
    let records = []
    for (let i = 0; i < data.length; i++) {
      const recordData = data[i]
      let recordInfo = parser.parseRecordData(recordData)
      // UPDATE previous status info
      // otherwise, it will erase `codec` to null
      if (state.eventsRecords[eventId] instanceof Array) {
        if (state.eventsRecords[eventId][i] instanceof Object) {
          recordInfo.statusInfo = state.eventsRecords[eventId][i].statusInfo
        }
      }
      records.push(recordInfo)
    }
    Vue.set(state.eventsRecords, [eventId], records)
    // getRecordMedia가 재생 시점에 view URL을 발급할 때 사용할 siteId를 기억해 둔다.
    if (siteId !== undefined) Vue.set(state.eventsSiteIds, [eventId], siteId)
    state.status.getEventRecords = "successful"
  },
  [GET_EVENT_RECORDS_FAILURE]: function (state) {
    state.status.getEventRecords = "failed"
  },
  [GET_RECORD_MEDIA_REQUEST]: function (state, {recordId}) {
    Vue.set(state.status.getRecordMedia, [recordId], "requested")
  },
  [GET_RECORD_MEDIA_SUCCESS]: function (state, {eventId, recordIndex, recordId, blob, codec}) {
    // UPDATE - recordMedia
    let urlCreator = window.URL || window.webkitURL
    let mediaUrl = urlCreator.createObjectURL(blob)
    state.recordsMedia = {...state.recordsMedia, [recordId]: mediaUrl}
    Vue.set(state.status.getRecordMedia, [recordId], "successful")
    
    // UPDATE - eventRecord
    state.eventsRecords[eventId][recordIndex].statusInfo.codec = codec
    state.eventsRecords[eventId][recordIndex].statusInfo.completed = true
    state.eventsRecords[eventId][recordIndex].statusInfo.downloaded = true
    state.eventsRecords[eventId][recordIndex].statusInfo.reallyUploaded = true
  },
  [GET_RECORD_MEDIA_FAILURE]: function (state, {recordId}) {
    Vue.set(state.status.getRecordMedia, [recordId], "failed")
  },
}

export default {
  state,
  getters,
  actions,
  mutations
}
